/**
 * PromoFrameworkSidebar — shows next-level competencies and behaviors
 * when a promotion is being considered
 */
import { C, BEHAVIORS, FINN_VALUES, ALL_LEVELS, LEADERSHIP_VALUES, getBehaviorGroup, getCompetencies, getCompetencyDescription, getLeadershipValueGroup, getTrack } from '../../data/managerReviewData';
import type { Employee } from '../../types/managerReviewV2';

const renderBold = (text: string): React.ReactNode => {
  if (!text || !text.includes('**')) return text;
  const parts = text.split('**');
  return parts.map((p, i) => i % 2 === 1 ? <strong key={i}>{p}</strong> : p);
};

const parseSentences = (text: string | null | undefined): string[] => {
  if (!text) return [];
  const cleaned = text.replace(/\n\*\*\n/g, '\n\n').replace(/\n\*\*$/, '').replace(/^\*\*\n/, '');
  if (cleaned.includes('\n\n')) return cleaned.split('\n\n').map(s => s.trim()).filter(Boolean);
  if (cleaned.includes('\n')) return cleaned.split('\n').map(s => s.trim()).filter(Boolean);
  return cleaned.split(/\. (?=[A-Z])/).map((s, i, a) => i < a.length - 1 ? s + '.' : s).filter(Boolean);
};

interface PromoFrameworkSidebarProps {
  employee: Employee;
  level: string;
  promoTrack: 'E' | 'M';
  setPromoTrack: (t: 'E' | 'M') => void;
}

export default function PromoFrameworkSidebar({ employee, level, promoTrack, setPromoTrack }: PromoFrameworkSidebarProps) {
  const isIC4 = level === 'IC4';
  const levelIdx = ALL_LEVELS.indexOf(level as typeof ALL_LEVELS[number]);
  const nextLv = isIC4 ? (promoTrack === 'E' ? 'E5' : 'M5') : (levelIdx >= 0 && levelIdx < ALL_LEVELS.length - 1 ? ALL_LEVELS[levelIdx + 1] : null);
  const nextIsM = nextLv ? getTrack(nextLv) === 'M' : false;
  const nextRoleComps = nextLv ? (getCompetencies(employee.role, nextLv) || []) : [];
  const nextBehaviorData = nextLv ? (BEHAVIORS[getBehaviorGroup(nextLv)] || BEHAVIORS['IC3']) : null;

  if (!nextLv) {
    return <div style={{ padding: 24, color: C.muted, fontSize: 13 }}>No next level available.</div>;
  }

  const sidebarLabel = (txt: string) => (
    <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#999', marginBottom: 10 }}>{txt}</div>
  );

  return (
    <div style={{ padding: '20px 20px 40px' }}>
      <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 6 }}>{employee.role}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
        <span style={{ fontSize: 11, color: C.muted }}>After promotion →</span>
        <span style={{
          fontSize: 11, fontWeight: 700, color: '#1a7a38',
          background: 'rgba(38,189,89,0.1)', border: '1px solid rgba(38,189,89,0.35)',
          borderRadius: 5, padding: '2px 8px',
        }}>{nextLv}</span>
      </div>

      {isIC4 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 18 }}>
          <span style={{ fontSize: 11, color: C.muted }}>Track:</span>
          {(['E', 'M'] as const).map(t => (
            <button key={t} onClick={() => setPromoTrack(t)} style={{
              padding: '3px 10px', borderRadius: 12,
              border: `1.5px solid ${promoTrack === t ? '#1a1a1a' : C.border}`,
              background: promoTrack === t ? '#1a1a1a' : '#fff',
              color: promoTrack === t ? '#fff' : C.muted,
              fontSize: 12, fontWeight: promoTrack === t ? 700 : 400,
              cursor: 'pointer', fontFamily: 'Inter',
            }}>
              {t === 'E' ? 'Expert (E5)' : 'Leadership (M5)'}
            </button>
          ))}
        </div>
      )}

      {sidebarLabel('Competencies')}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
        {nextIsM
          ? Object.entries(LEADERSHIP_VALUES[getLeadershipValueGroup(nextLv)] || {}).map(([name, desc]) => {
              const sentences = parseSentences(desc);
              return (
                <div key={name} style={{ background: '#fff', border: '1px solid #e4e4e4', borderRadius: 8, padding: '10px 12px' }}>
                  <div style={{ fontWeight: 700, fontSize: 12, marginBottom: sentences.length ? 7 : 0 }}>{name}</div>
                  {sentences.length > 0 && (
                    <ul style={{ margin: 0, paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 3, listStyleType: 'disc' }}>
                      {sentences.map((s, i) => <li key={i} style={{ fontSize: 12, color: '#444', lineHeight: 1.6 }}>{renderBold(s)}</li>)}
                    </ul>
                  )}
                </div>
              );
            })
          : nextRoleComps.map(c => {
              const sentences = parseSentences(getCompetencyDescription(c, nextLv, employee.role));
              return (
                <div key={c} style={{ background: '#fff', border: '1px solid #e4e4e4', borderRadius: 8, padding: '10px 12px' }}>
                  <div style={{ fontWeight: 700, fontSize: 12, marginBottom: sentences.length ? 7 : 0 }}>{c}</div>
                  {sentences.length > 0 && (
                    <ul style={{ margin: 0, paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 3, listStyleType: 'disc' }}>
                      {sentences.map((s, i) => <li key={i} style={{ fontSize: 12, color: '#444', lineHeight: 1.6 }}>{renderBold(s)}</li>)}
                    </ul>
                  )}
                </div>
              );
            })
        }
      </div>

      {sidebarLabel('Behaviors')}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {nextBehaviorData && FINN_VALUES.map(v => {
          const bullets = nextBehaviorData[v.name] || [];
          return (
            <div key={v.name} style={{ background: '#fff', border: '1px solid #e4e4e4', borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ fontWeight: 700, fontSize: 12, marginBottom: bullets.length ? 7 : 0 }}>{v.name}</div>
              {bullets.length > 0 && (
                <ul style={{ margin: 0, paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {bullets.map((b, i) => <li key={i} style={{ fontSize: 12, color: '#444', lineHeight: 1.6 }}>{renderBold(b)}</li>)}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
